import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api, { getAccessToken, clearAccessToken, tryRefreshToken } from '../utils/api.js';
import { useTenant } from './TenantContext.js';

const AuthContext = createContext(null);

/**
 * AuthProvider - 認証状態をアプリ全体に提供
 *
 * - 初回マウント時に /auth/me を呼んでユーザー情報を取得
 * - タブフォーカス復帰時（visibilitychange）に自動で再取得
 * - ページ遷移時はキャッシュを利用（API呼び出しなし）
 */
export const AuthProvider = ({ children }) => {
    const { tenantSlug } = useTenant();
    const [currentUser, setCurrentUser] = useState(null);
    const [isInitializing, setIsInitializing] = useState(true);

    const fetchCurrentUser = useCallback(async () => {
        try {
            // トークンがない場合、Cookieからリフレッシュを試みる
            if (!getAccessToken()) {
                const refreshed = await tryRefreshToken();
                if (!refreshed) {
                    setCurrentUser(null);
                    return null;
                }
            }

            const response = await api.fetch('/auth/me');
            if (response && response.ok) {
                const user = await response.json();
                // テナント検証: URLのslugとユーザーのslugが一致しなければセッションクリア
                if (tenantSlug && user.tenant_slug && user.tenant_slug !== tenantSlug) {
                    console.warn('Tenant mismatch detected. Clearing session.');
                    try {
                        await api.fetch('/auth/logout', { method: 'POST' });
                    } catch (e) {
                        // logout失敗してもセッションクリアは続行
                    }
                    clearAccessToken();
                    setCurrentUser(null);
                    return null;
                }
                setCurrentUser(user);
                return user;
            } else {
                setCurrentUser(null);
                return null;
            }
        } catch (err) {
            console.error('Failed to fetch user:', err);
            setCurrentUser(null);
            return null;
        } finally {
            setIsInitializing(false);
        }
    }, [tenantSlug]);

    // 初回: tenantSlug 確定後にユーザー情報を取得
    useEffect(() => {
        if (tenantSlug === null) return;
        fetchCurrentUser();
    }, [tenantSlug, fetchCurrentUser]);

    // タブフォーカス復帰時に再取得（SWR の revalidateOnFocus と同等）
    // useRef でイベントリスナー内から最新の状態を参照（stale closure 防止 + リスナー再登録回避）
    const currentUserRef = useRef(currentUser);
    const fetchCurrentUserRef = useRef(fetchCurrentUser);
    useEffect(() => { currentUserRef.current = currentUser; }, [currentUser]);
    useEffect(() => { fetchCurrentUserRef.current = fetchCurrentUser; }, [fetchCurrentUser]);

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible' && currentUserRef.current) {
                fetchCurrentUserRef.current();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, []); // 依存配列を空にし、リスナー登録を1回のみに

    const logout = useCallback(async () => {
        try {
            await api.fetch('/auth/logout', { method: 'POST' });
        } catch (err) {
            console.error('Logout failed:', err);
        }
        clearAccessToken();
        setCurrentUser(null);
        window.location.href = '/login';
    }, []);

    const value = {
        currentUser,
        isInitializing,
        refreshUser: fetchCurrentUser,
        logout,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

/**
 * useAuth - 認証情報を取得するカスタムフック
 */
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export default AuthContext;
